import { useCallback, useEffect, useMemo, useState } from "react";
import { useOnboarding } from "@/onboarding/context";
import {
  DIFFICULTY_OPTIONS, DIFFICULTY_PROMPT, JOURNEY, MOMENT_SECTIONS, NEED_HELP_MESSAGE, REASSURANCE, TRANSITIONS,
  deriveExpanded, entryFor, stageIndex, transitionFor,
} from "@/lib/orientation";

// ORIENTATION LAYER (Beta revision) — orientation happens at MOMENTS, then disappears: a short popup on first
// entry and at each of the four genuine major transitions (once each, never on recursion back), a small
// I'M LOST control that reconstructs orientation from current state on demand, and a minimal proactive
// difficulty prompt when the host has strong existing evidence the current scaffold failed. No persistent bar.
// Purely observational over existing TWS state; help always routes back to the EXISTING coach (no second coach).

export const INTRO_KEY = "compass_orient_intro_seen";   // legacy first-encounter flag (still set by SessionRoadmap)
const memKey = (sid) => `compass_orient_${sid}`;
const loadMem = (sid) => { try { return JSON.parse(localStorage.getItem(memKey(sid)) || "{}") || {}; } catch { return {}; } };
const saveMem = (sid, m) => { try { localStorage.setItem(memKey(sid), JSON.stringify(m)); } catch { /* ignore */ } };
// ONCE PER GENUINE TRANSITION — a second memory that does not depend on localStorage or on this component instance: every popup
// key shown in this page load (entry / each boundary), per session. A remount, a double-invoked effect, a transient stage dip or a
// storage failure can therefore never show the same orientation twice.
const shownThisLoad = new Set();
const popupKey = (sid, key) => `${sid}|${key}`;
export const _resetShownForTests = () => shownThisLoad.clear();
// STRUCTURE VIEW, FIRST USE — once per browser (a returning student is not re-told), reopenable any time from a quiet control
export const STRUCTURE_SEEN_KEY = "compass_orient_structure_seen";
export const STRUCTURE_ORIENTATION = {
  title: "This is the same essay, shown by its parts.",
  lines: ["Click a part to work with it. You can move it, split it, join it with another part, or show where it belongs in the whole. Anything you change here changes the same writing you see in Essay view."],
};
// STRUCTURE VIEW WHEN ONLY THE OPENING EXISTS (the process is Organize my writing as a whole): the natural moment "I've written my opening,
// but I don't know how the rest should be organized" — one brief orientation; the DB (controller-led) may then ask its one question.
export const OPENING_ONLY_ORIENTATION = {
  title: "You've started your opening.",
  lines: ["Now think about what the rest of the essay needs to do to support your position."],
};
// FIRST USE of the writing view and of Consolidate each part (consolidated Full Studio): 1\u20132 warm sentences, once per browser, then out of the way
export const WRITING_SEEN_KEY = "compass_orient_writing_seen";
export const WRITING_ORIENTATION = null;   // retired: the Essay Mode quick orientation (onboarding layer) replaced the "Your writing" popup
export const CONSOLIDATE_SEEN_KEY = "compass_orient_consolidate_seen";
export const CONSOLIDATE_ORIENTATION = { title: "Here we'll look closely at one section of your writing.", lines: ["You’re consolidating this section right now — making it do what the whole essay needs it to do."] };
const seenOnce = (key) => { let seen = false; try { seen = !!localStorage.getItem(key); } catch { /* ignore */ } return seen; };
const markSeen = (key) => { try { localStorage.setItem(key, "1"); } catch { /* ignore */ } };

// Return the student to the EXISTING dialogue input (the student explains in their own words).
const focusExistingInput = () => {
  const el = document.querySelector('[data-testid="ask-compass-input"], [data-testid="tws-chat-input"], [data-testid="fo-input"]');
  if (el) { try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* ignore */ } el.focus(); return true; }
  return false;
};

const BTN = "rounded-sm bg-[#8C3A2A] px-5 py-2 text-[12.5px] uppercase tracking-[0.12em] text-white hover:bg-[#6B2C20] transition-colors";
const BTN_QUIET = "rounded-sm border border-stone-300 bg-white px-5 py-2 text-[12.5px] uppercase tracking-[0.12em] text-stone-700 hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors";

const Popup = ({ testid, dataKey, kicker, title, lines = [], sections, onEscape, children }) => (
  <div data-testid="orientation-popup" role="dialog" aria-modal="true" aria-label={title} onKeyDown={(e) => { if (e.key === "Escape" && onEscape) onEscape(); }}
    className="fixed inset-0 z-[70] flex items-center justify-center bg-stone-900/40 p-4">
    <div data-testid={testid} data-key={dataKey} className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-md border border-stone-200 bg-white px-6 py-6 sm:px-8 sm:py-7 shadow-xl">
      {kicker && <p className="font-mono-panel text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-2">{kicker}</p>}
      <p className="font-serif-display font-bold text-[1.35rem] sm:text-[1.5rem] leading-snug text-stone-900 mb-3">{title}</p>
      {sections ? (
        <dl className="space-y-3">
          {MOMENT_SECTIONS.map((s) => (
            <div key={s.key} data-testid={`orientation-section-${s.key}`}>
              <dt className="font-mono-panel font-bold text-[10px] uppercase tracking-[0.18em] text-stone-500 mb-0.5">{s.label}</dt>
              <dd className="text-[14px] leading-relaxed text-stone-700">{sections[s.key]}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <div className="space-y-2 text-[14px] leading-relaxed text-stone-700">{lines.filter(Boolean).map((l) => <p key={l}>{l}</p>)}</div>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-2.5">{children}</div>
    </div>
  </div>
);

export default function OrientationLayer({ signals, onSendToCoach, difficulty, onDifficultyDone, className = "" }) {
  const sid = signals?.sessionId || "";
  const x = useMemo(() => deriveExpanded(signals || {}), [signals]);
  const [modal, setModal] = useState(null);   // {type: entry | transition | lost | difficulty | why, ...}

  // Entry once per session; each major transition once, only on a genuine forward boundary crossing. An automatic
  // popup NEVER interrupts one that is open (entry, I'M LOST, difficulty…): while a popup is up the stage memory is
  // left as it was, so a due transition is evaluated again — once — when the student has dismissed the current one.
  // AUTHENTIC REPAIR 1 (popup twice): (a) nothing is evaluated until the host says the workspace state is loaded
  // (`signals.ready === false` = still loading — an empty workspace must not read as "develop"); (b) entering at a
  // later stage marks every boundary up to that stage as already crossed, so the entry text is never repeated as a
  // transition after a reload or a transient dip.
  const onboarding = useOnboarding();
  const tourSuppressed = !!(onboarding && (onboarding.active || onboarding.demoOpen));   // the guided walkthrough speaks instead; nothing is marked seen meanwhile
  useEffect(() => {
    // CONSOLIDATED LOOP (essay-as-workspace): writing / development / organization are seamless and recursive — no announced stages, so no
    // entry popup and no stage-transition popup. I'M LOST and the difficulty prompt stay (on demand / on evidence).
    if (tourSuppressed || !sid || (signals && signals.ready === false) || (signals && signals.ot && signals.ot.consolidated)) return;
    const mem = loadMem(sid);
    const next = { ...mem, seenTransitions: { ...(mem.seenTransitions || {}) } };
    if (!mem.entrySeen) {
      next.entrySeen = true;
      next.lastStage = x.stage;
      for (let k = 1; k <= stageIndex(x.stage); k += 1) next.seenTransitions[`${JOURNEY[k - 1].key}>${JOURNEY[k].key}`] = true;
      saveMem(sid, next);
      // an entry at a later stage IS that stage's boundary text — the same key a transition into it would use
      const key = stageIndex(x.stage) > 0 ? `${JOURNEY[stageIndex(x.stage) - 1].key}>${x.stage}` : "entry:develop";
      if (!shownThisLoad.has(popupKey(sid, key))) {
        shownThisLoad.add(popupKey(sid, key));
        setModal({ type: "entry", stage: x.stage, content: entryFor(signals || {}) });
      }
      return;
    }
    if (modal) return;
    const t = transitionFor(mem.lastStage, x.stage);
    if (t && !next.seenTransitions[t] && !shownThisLoad.has(popupKey(sid, t))) {
      next.seenTransitions[t] = true;
      shownThisLoad.add(popupKey(sid, t));
      setModal({ type: "transition", key: t });
    } else if (t) {
      next.seenTransitions[t] = true;
    }
    next.lastStage = x.stage;
    saveMem(sid, next);
  }, [sid, x.stage, modal, signals && signals.ready]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (difficulty) setModal({ type: "difficulty" }); }, [difficulty]);
  // STRUCTURE VIEW OPENED INTENTIONALLY (the essay is the default in the consolidated loop): the first-use explanation, once — the same
  // popup / dismissal path as every other orientation moment; "How to use Structure View" reopens it later.
  const structureView = !!(signals && signals.ot && signals.ot.structureView && signals.ot.consolidated);
  const openingOnly = !!(signals && signals.ot && signals.ot.openingOnly);
  useEffect(() => {
    if (tourSuppressed) return;
    if (!structureView || modal) return;
    if (seenOnce(STRUCTURE_SEEN_KEY) || shownThisLoad.has(popupKey("*", "structure"))) return;
    shownThisLoad.add(popupKey("*", "structure"));
    if (openingOnly) shownThisLoad.add(popupKey(sid, "opening_only"));          // the opening-only line rides inside this popup
    markSeen(STRUCTURE_SEEN_KEY);
    setModal({ type: "structure", openingOnly });
  }, [structureView, modal, openingOnly, sid]);
  // the opening-only moment may also arrive AFTER the general structure orientation was seen: once per session
  useEffect(() => {
    if (tourSuppressed) return;
    if (!structureView || !openingOnly || modal || !sid) return;
    if (shownThisLoad.has(popupKey(sid, "opening_only")) || !seenOnce(STRUCTURE_SEEN_KEY)) return;
    shownThisLoad.add(popupKey(sid, "opening_only"));
    setModal({ type: "opening_only" });
  }, [structureView, openingOnly, modal, sid]);
  // WRITING VIEW, first use (consolidated Full Studio, essay view, nothing written yet)
  const writingView = !!(signals && signals.ot && signals.ot.consolidated && signals.screen === "ot" && !signals.ot.structureView && signals.ready !== false
    && !(signals.ot.items || []).some((i) => (i.text || "").trim()) && !signals.ot.hasThesis);          // nothing written yet: the FIRST use
  // ESSAY MODE QUICK ORIENTATION (4 spotlight beats on the REAL workspace) replaces the old "Your writing" popup: the onboarding layer shows it once,
  // and only for a student who bypassed the guided demo (essayOrientationSeen false AND initialTourCompleted false) — alternative paths, not sequential
  useEffect(() => {
    if (tourSuppressed || !writingView || modal || !onboarding) return;
    if (shownThisLoad.has(popupKey("*", "writing"))) return;
    shownThisLoad.add(popupKey("*", "writing"));
    onboarding.startEssayOrientation();
  }, [writingView, modal, tourSuppressed, onboarding]);
  // CONSOLIDATE EACH PART, first use (a part is focused)
  const consolidating = !!(signals && signals.ot && signals.ot.consolidated && signals.ot.composeFocused);
  useEffect(() => {
    if (tourSuppressed) return;
    if (!consolidating || modal) return;
    if (seenOnce(CONSOLIDATE_SEEN_KEY) || shownThisLoad.has(popupKey("*", "consolidate"))) return;
    shownThisLoad.add(popupKey("*", "consolidate"));
    markSeen(CONSOLIDATE_SEEN_KEY);
    setModal({ type: "consolidate" });
  }, [consolidating, modal]);

  const close = useCallback(() => setModal(null), []);
  const toCoach = useCallback(async (text) => { if (onSendToCoach) await onSendToCoach(text); else focusExistingInput(); }, [onSendToCoach]);
  const finishDifficulty = useCallback((result) => { setModal(null); if (onDifficultyDone) onDifficultyDone(result); }, [onDifficultyDone]);
  const choose = useCallback((opt) => {
    if (opt.route === "coach") { finishDifficulty("sent"); toCoach(opt.message); return; }
    if (opt.route === "orient") { setModal({ type: "why", message: opt.message }); return; }
    finishDifficulty("kept"); focusExistingInput();
  }, [finishDifficulty, toCoach]);

  const action = x.action ? `Right now you're ${x.action}.` : "";
  const label = (stage) => JOURNEY[stageIndex(stage)]?.label || "";

  return (
    <div data-testid="orientation-layer" data-stage={x.stage} className={`flex items-center justify-end gap-4 ${className}`}>
      {structureView ? (
        <button data-testid="structure-view-help" onClick={() => setModal({ type: "structure" })}
          className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-stone-400 hover:text-[#8C3A2A] transition-colors">How to use Structure View</button>
      ) : null}
      <button data-testid="orientation-lost" data-tour="im-lost" onClick={() => setModal({ type: "lost" })}
        className="font-mono-panel text-[10px] uppercase tracking-[0.18em] text-stone-400 hover:text-[#8C3A2A] transition-colors">I'M LOST</button>

      {modal?.type === "structure" && (
        <Popup testid="orientation-structure" kicker="Structure view" title={STRUCTURE_ORIENTATION.title}
          lines={modal.openingOnly ? [...STRUCTURE_ORIENTATION.lines, `${OPENING_ONLY_ORIENTATION.title} ${OPENING_ONLY_ORIENTATION.lines[0]}`] : STRUCTURE_ORIENTATION.lines} onEscape={close}>
          <button autoFocus data-testid="orientation-structure-got-it" onClick={close} className={BTN}>Got it</button>
        </Popup>
      )}
      {modal?.type === "opening_only" && (
        <Popup testid="orientation-opening-only" kicker="Structure view" title={OPENING_ONLY_ORIENTATION.title} lines={OPENING_ONLY_ORIENTATION.lines} onEscape={close}>
          <button autoFocus data-testid="orientation-opening-only-got-it" onClick={close} className={BTN}>Got it</button>
        </Popup>
      )}
      {modal?.type === "consolidate" && (
        <Popup testid="orientation-consolidate" kicker="Consolidate Sections" title={CONSOLIDATE_ORIENTATION.title} lines={CONSOLIDATE_ORIENTATION.lines} onEscape={close}>
          <button autoFocus data-testid="orientation-consolidate-got-it" onClick={close} className={BTN}>Got it</button>
        </Popup>
      )}
      {modal?.type === "entry" && modal.content && (
        <Popup testid="orientation-entry" dataKey={modal.stage} kicker={label(modal.stage)} title={modal.content.title} sections={modal.content} onEscape={close}>
          <button autoFocus data-testid="orientation-popup-continue" onClick={close} className={BTN}>Got it — let's go</button>
        </Popup>
      )}
      {modal?.type === "transition" && (
        <Popup testid="orientation-transition" dataKey={modal.key} kicker={label(modal.key.split(">")[1])}
          title={TRANSITIONS[modal.key].title} sections={TRANSITIONS[modal.key]} onEscape={close}>
          <button autoFocus data-testid="orientation-popup-continue" onClick={close} className={BTN}>Got it — let's go</button>
        </Popup>
      )}
      {modal?.type === "lost" && (
        <Popup testid="orientation-lost-panel" kicker="Let's get you oriented." title={x.where}
          lines={[action, x.how, x.why, x.howLong, x.next, REASSURANCE]} onEscape={close}>
          <button autoFocus data-testid="orientation-got-it" onClick={close} className={BTN}>Got it</button>
          <button data-testid="orientation-need-help" onClick={() => { close(); toCoach(NEED_HELP_MESSAGE); }} className={BTN_QUIET}>I need help doing that</button>
        </Popup>
      )}
      {modal?.type === "difficulty" && (
        <Popup testid="orientation-difficulty" title={DIFFICULTY_PROMPT[0]} lines={[DIFFICULTY_PROMPT[1]]} onEscape={() => finishDifficulty("kept")}>
          <div className="flex w-full flex-col items-stretch gap-2">
            {DIFFICULTY_OPTIONS.map((o, i) => (
              <button key={o.key} autoFocus={i === 0} data-testid={`orientation-difficulty-${o.key}`} onClick={() => choose(o)}
                className="rounded-sm border border-stone-300 bg-white px-4 py-2.5 text-left text-[13.5px] text-stone-800 hover:border-[#8C3A2A] hover:text-[#8C3A2A] transition-colors">{o.label}</button>
            ))}
          </div>
        </Popup>
      )}
      {modal?.type === "why" && (
        <Popup testid="orientation-why" kicker="How this connects to your paper" title={x.where}
          lines={[action, x.why, x.howLong, x.next, REASSURANCE]} onEscape={() => finishDifficulty("kept")}>
          <button autoFocus data-testid="orientation-got-it" onClick={() => finishDifficulty("kept")} className={BTN}>Got it</button>
          <button data-testid="orientation-need-help" onClick={() => { const m = modal.message; finishDifficulty("sent"); toCoach(m); }} className={BTN_QUIET}>I need help doing that</button>
        </Popup>
      )}
    </div>
  );
}
